import {
    Button,
    Checkbox,
    Container,
    Icon,
    MenuItem,
    Paper,
    RichTextArea,
    Select,
    Stack,
    Text,
    TextArea,
    TextInput,
    Tooltip,
    styled,
} from 'shell/components';
import { DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount, getTooltipComponent } from 'utils/helper';

const TabletForm = ({ form, errors, onChange, onClickAddCriterion, onClickDeleteCriterion }) => {
    return (
        <CustomContainer device={'tablet'}>
            <RootStack alignItems={'flex-start'} spacing={5}>
                <Stack spacing={2}>
                    <Stack direction={'row'} spacing={4}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            Create a Rubric with AI
                        </Text>
                        {/* <Button startIcon={<Icon kind={'video'} />} variant={'borderless'}>
                            Best Practices for Rubric Creation
                        </Button> */}
                    </Stack>
                    <Text>
                        Our AI leverages your assignment title and description to generate a rubric. You have the option
                        to specify criteria manually or allow the AI to suggest criteria based on this information.
                    </Text>
                </Stack>
                <TextInput
                    sx={{ width: '650px' }}
                    error={Boolean(errors.title)}
                    helperText={errors.title}
                    label={'Assignment Title'}
                    placeholder={'ex. Create a Marketing Plan'}
                    required
                    value={form.title}
                    onChange={(e) => onChange('title', e.target.value)}
                />
                <RichTextArea
                    sx={{ width: '650px' }}
                    error={Boolean(errors.description)}
                    label={'Assignment Description'}
                    helperText={
                        <Stack component={'span'} direction={'row-reverse'} justifyContent={'space-between'}>
                            <Text color={'inherit'} component={'span'} variant={'p3'}>
                                {getHTMLWordCount(form.description)}/{DESCRIPTION_WORD_COUNT_LIMIT} words
                            </Text>
                            <Text color={'inherit'} component={'span'} variant={'p3'}>
                                {errors.description}
                            </Text>
                        </Stack>
                    }
                    helperTextPosition={'end'}
                    placeholder={
                        'Provide an overview of the tasks, requirements, and expectations for this assignment. This will be used as context to help our AI generate an accurate rubric for this assignment.'
                    }
                    required
                    value={form.description}
                    onChange={(html) => onChange('description', html)}
                />
                <Stack
                    sx={{ width: '100%' }}
                    alignItems={'flex-end'}
                    direction={'row'}
                    justifyContent={'space-between'}
                >
                    <Stack alignItems={'flex-end'} direction={'row'} spacing={4}>
                        <CustomSelect
                            disabled={form.useCustomCriteria}
                            label={
                                <Stack alignItems={'center'} component={'span'} direction={'row'} spacing={2}>
                                    <Text>How many criteria will this rubric have?</Text>
                                    <CustomTooltip
                                        content={getTooltipComponent(
                                            'Creating criteria',
                                            'Please indicate the number of criteria you wish to create. If you don’t choose to specify your own criteria, they will be automatically generated based on the assignment description using AI. We recommend 5 or fewer for best results.'
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'num-criteria-select'}
                            required
                            value={form.useCustomCriteria ? form.criteria.length.toString() : form.numCriteria}
                            onChange={(e) => onChange('numCriteria', e.target.value)}
                        >
                            <MenuItem value={'1'}>1</MenuItem>
                            <MenuItem value={'2'}>2</MenuItem>
                            <MenuItem value={'3'}>3</MenuItem>
                            <MenuItem value={'4'}>4</MenuItem>
                            <MenuItem value={'5'}>5</MenuItem>
                            <MenuItem value={'6'}>6</MenuItem>
                            <MenuItem value={'7'}>7</MenuItem>
                            <MenuItem value={'8'}>8</MenuItem>
                            <MenuItem value={'9'}>9</MenuItem>
                            <MenuItem value={'10'}>10</MenuItem>
                        </CustomSelect>
                        <Checkbox
                            aria-label={'Specify my own criteria'}
                            checked={form.useCustomCriteria}
                            label={'Specify my own criteria'}
                            onChange={(_, checked) => onChange('useCustomCriteria', checked)}
                        />
                    </Stack>
                    {form.useCustomCriteria && (
                        <Button startIcon={<Icon kind={'add'} />} variant={'secondary'} onClick={onClickAddCriterion}>
                            Add criterion
                        </Button>
                    )}
                </Stack>
                {form.useCustomCriteria && (
                    <Stack sx={{ width: '100%' }} spacing={4}>
                        {form.criteria.map((criterion, index) => (
                            <CustomPaper key={criterion.id} variant={'outlined'}>
                                <Stack spacing={4}>
                                    <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                        <TextInput
                                            sx={{ width: '400px' }}
                                            error={Boolean(errors.criteria?.[index]?.title)}
                                            helperText={errors.criteria?.[index]?.title}
                                            label={'Criterion Name'}
                                            placeholder={'ex. Organization'}
                                            required
                                            value={form.criteria.find((c) => c.id === criterion.id).title}
                                            onChange={(e) => {
                                                onChange(
                                                    'criteria',
                                                    form.criteria.map((c) =>
                                                        c.id === criterion.id ? { ...c, title: e.target.value } : c
                                                    )
                                                );
                                            }}
                                        />
                                        <Button
                                            color={'neutral'}
                                            disabled={form.criteria.length <= 2}
                                            startIcon={<Icon kind={'delete'} />}
                                            variant={'tertiary'}
                                            onClick={() => onClickDeleteCriterion(criterion)}
                                        >
                                            Delete Criterion
                                        </Button>
                                    </Stack>
                                    <TextArea
                                        helperText={
                                            'If left blank, the AI will create a description for this criterion'
                                        }
                                        label={'Criterion Description'}
                                        placeholder={
                                            'ex. Evaluates the clarity and coherence of the project structure, ensuring logical arrangement and easy comprehension.'
                                        }
                                        rows={4}
                                        value={form.criteria.find((c) => c.id === criterion.id).description}
                                        onChange={(e) => {
                                            onChange(
                                                'criteria',
                                                form.criteria.map((c) =>
                                                    c.id === criterion.id ? { ...c, description: e.target.value } : c
                                                )
                                            );
                                        }}
                                    />
                                </Stack>
                            </CustomPaper>
                        ))}
                    </Stack>
                )}
                <CustomTextInput
                    error={Boolean(errors.maxScore)}
                    helperText={errors.maxScore}
                    label={'Max score for each criterion'}
                    placeholder={'ex. 25'}
                    required
                    value={form.maxScore}
                    onChange={(e) => onChange('maxScore', e.target.value)}
                />
                <CustomSelect
                    label={
                        <Stack alignItems={'center'} component={'span'} direction={'row'} spacing={2}>
                            <Text>How many rating levels will each criterion have?</Text>
                            <CustomTooltip
                                content={getTooltipComponent(
                                    'What are rating levels?',
                                    'Rating levels in a rubric indicate the varying degrees of achievement or performance associated with each criterion. We recommend 8 or fewer for best results.'
                                )}
                                type={'info'}
                            />
                        </Stack>
                    }
                    labelId={'num-options-select'}
                    required
                    value={form.numOptions}
                    onChange={(e) => onChange('numOptions', e.target.value)}
                >
                    <MenuItem value={'2'}>2</MenuItem>
                    <MenuItem value={'3'}>3</MenuItem>
                    <MenuItem value={'4'}>4</MenuItem>
                    <MenuItem value={'5'}>5</MenuItem>
                    <MenuItem value={'6'}>6</MenuItem>
                    <MenuItem value={'7'}>7</MenuItem>
                    <MenuItem value={'8'}>8</MenuItem>
                    <MenuItem value={'9'}>9</MenuItem>
                    <MenuItem value={'10'}>10</MenuItem>
                </CustomSelect>
            </RootStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(10),
        background: theme.palette.ensightful.shades[0],
    };
});

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiFormLabel-root': {
            display: 'flex',
        },
        '& > .MuiInputBase-root': {
            width: '300px',
        },
    };
});

const CustomTooltip = styled(Tooltip)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '300px',
    };
});

export default TabletForm;
